/**
 * Pause dialling for one account, from the screen where you would want to.
 *
 * Arun: *"can you give me the pause Call backs at an account levle for each
 * customer acccount inside the callback quue (maybe top right or soemwhere -
 * Callback Active is default state)?"* and *"pasue this account -> Pause retries -
 * would be good if you can make this more obvious than hide here."*
 *
 * It was a switch on the third section of a settings tab. That is the wrong place
 * for it: pausing is something you do *while looking at the queue*, usually
 * because a customer has just asked you to stop, and hunting through configuration
 * to find the switch is time spent while calls keep going out.
 *
 * Two decisions:
 *
 * - **The state is spelled out, and the button is the action.** A single toggle
 *   whose label is its current state is ambiguous at exactly the wrong moment — is
 *   "Callbacks active" telling me what is true, or what pressing it will do? So
 *   the pill states the fact and the button beside it states the change.
 * - **Pausing asks; resuming does not.** Nothing about pausing is destructive, but
 *   it is silent: no call fails, no row errors, the queue simply stops draining,
 *   and an accidental pause could go unnoticed for a day. The dialog is there to
 *   say how many calls it affects. Resuming needs no ceremony — it is the state
 *   the account is supposed to be in.
 */

import { useState } from "react";
import { PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorMessage, type AccountConfig } from "@/lib/api";
import { useUpdateAccount } from "@/lib/queries";

export function PauseControl({
  accountId,
  config,
  /** Callbacks whose time has already passed — what a pause holds back this minute. */
  dueNow,
  /**
   * Everything scheduled or in flight. Needed because `dueNow` is 0 most of the
   * time, and "nothing is due" printed over a table of two scheduled calls reads
   * as a straight contradiction of the screen behind the dialog.
   */
  pending,
}: {
  accountId: string;
  config: AccountConfig;
  dueNow: number;
  pending: number;
}) {
  const update = useUpdateAccount(accountId);
  const [confirming, setConfirming] = useState(false);
  const paused = config.account.paused;

  const setPaused = (next: boolean) =>
    update.mutate(
      { paused: next },
      {
        onSuccess: () => {
          setConfirming(false);
          toast.success(
            next
              ? "Callbacks paused. Nothing will be dialled until you resume."
              : "Callbacks active again.",
          );
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );

  return (
    <>
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${
            paused
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : "border-border text-muted-foreground"
          }`}
        >
          <span
            aria-hidden
            className={`size-1.5 rounded-full ${paused ? "bg-destructive" : "bg-success"}`}
          />
          {paused ? "Callbacks paused" : "Callbacks active"}
        </span>
        {paused ? (
          <Button size="sm" disabled={update.isPending} onClick={() => setPaused(false)}>
            <PlayCircle className="size-3.5" />
            Resume
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={update.isPending}
            onClick={() => setConfirming(true)}
          >
            <PauseCircle className="size-3.5" />
            Pause
          </Button>
        )}
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pause callbacks for {config.account.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  First calls and retries both stop. Everything the agents post keeps arriving and
                  keeps being scheduled — the queue simply stops draining, so nothing is lost and
                  nothing is dialled.
                </p>
                <p>
                  {dueNow > 0 ? (
                    <>
                      <span className="text-warning">
                        {dueNow} callback{dueNow === 1 ? "" : "s"} ready to go out now
                      </span>{" "}
                      will wait, along with the rest of the queue.
                    </>
                  ) : pending > 0 ? (
                    <>
                      Nothing is ready this minute. The {pending} scheduled callback
                      {pending === 1 ? "" : "s"} will wait instead of going out.
                    </>
                  ) : (
                    "There is nothing waiting to be dialled right now."
                  )}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>Keep calling</AlertDialogCancel>
            <AlertDialogAction
              disabled={update.isPending}
              onClick={(e) => {
                // The dialog closes itself on action; the mutation has to finish
                // before that or a failed PATCH would leave the pill claiming a
                // pause that never happened.
                e.preventDefault();
                setPaused(true);
              }}
            >
              {update.isPending ? "Pausing…" : "Pause callbacks"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
